import { initializeApp } from "firebase/app";
import { getMessaging, onMessage } from "firebase/messaging";
import "firebase/messaging";
import Vue from '@/main';
import store from '@/store';
import Toast from '@/components/toast/Toast';

/**
 * Per-entity Firebase config for MK1 web push.
 *
 * Replaces the old top-level `initializeApp(window.firebaseConfig)` which
 * relied on a static `<script src="/config/fcm.js">` baked into index.html.
 * That static file held a single shared Firebase project and could not be
 * overridden per entity.
 *
 * Now we fetch GET /api/member/v1/runtime/config at first use — the backend
 * resolves the entity from the request host (EntityProvider) and merges any
 * `entities_firebase_bindings.channel='web_all'` project config over the shared
 * config. Same mechanism MK2 uses, so one binding can drive both member versions.
 *
 * Init is lazy because consumers (NotificationsPermissionModal) only run when
 * the user opens the prompt — no need to load Firebase SDK eagerly at app
 * boot. Subsequent calls reuse the same messaging instance.
 */

let messagingPromise = null;
let firebaseProjectId = null;

async function fetchRuntimeFirebaseConfig() {
  const response = await fetch('/api/member/v1/runtime/config', {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`runtime/config HTTP ${response.status}`);
  }
  const body = await response.json();
  const cfg = body && body.firebaseConfig;
  if (!cfg || !cfg.apiKey || !cfg.projectId) {
    throw new Error('runtime/config returned no usable firebaseConfig');
  }
  return cfg;
}

export async function getFirebaseMessaging() {
  if (messagingPromise) return messagingPromise;

  messagingPromise = (async () => {
    const config = await fetchRuntimeFirebaseConfig();
    const firebaseApp = initializeApp(config);
    firebaseProjectId = firebaseApp.options.projectId || null;
    const messaging = getMessaging(firebaseApp);

    onMessage(messaging, (payload) => {
      const payloadUuid = payload.data.message;
      if (payloadUuid !== store.state.auth.entity.uuid || !store.state.auth.isAuthorized) {
        return;
      }
      Vue.$toast.info({
        component: Toast,
        props: {
          title: payload.data.title,
          body: payload.data.body,
          to: payload.data.url,
        },
      });
    });

    return messaging;
  })().catch((err) => {
    // Reset so the next call retries — relevant when runtime/config is
    // temporarily unreachable during cold start.
    messagingPromise = null;
    // eslint-disable-next-line no-console
    console.error('[firebase] init failed:', err);
    return null;
  });

  return messagingPromise;
}

/**
 * Firebase project_id of the currently-initialized app, or null when init
 * hasn't run yet / failed. Used by the push-token registration to tag rows
 * in `clients_push_tokens.firebase_project_id` so the backend knows which
 * Service Account signs sends to this token.
 */
export function getFirebaseProjectId() {
  return firebaseProjectId;
}

// Backwards-compat default export. Consumers should migrate to
// getFirebaseMessaging() — using this directly returns null until the first
// `await getFirebaseMessaging()` resolves.
export default null;
